import { useEffect, useRef, useState } from 'react';
import { REQUIRED_SLOTS } from '@adbonus/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { useDebounced } from '../lib/useDebounced';
import {
  useAcceptCar, useBookings, useCustomer, useCustomers, useInspectionRules, useServices, useAssignees,
} from '../lib/queries';
import {
  Alert, Button, Card, EmptyState, Field, Pill, SectionTitle, Select, TierBadge,
} from '../components/ui';
import { ServicePicker, type ServiceChoice } from '../components/ServicePicker';
import { DuePicker } from '../components/DueEditor';
import { InspectionCapture, type CaptureState } from '../components/inspection/InspectionCapture';
import { useInspectionWords } from '../components/inspection/labels';

/** `choice` is null until the counter has picked a service or typed a one-off. */
interface Row { key: string; choice: ServiceChoice | null; quantity: number; performedByAdminId: string | null }

const newRow = (): Row => ({
  key: crypto.randomUUID(), choice: null, quantity: 1, performedByAdminId: null,
});

/** A row counts only once it names something: a service, or a one-off with a name. */
const named = (row: Row): boolean =>
  row.choice !== null
  && (row.choice.kind === 'service' || row.choice.customName.trim().length > 0);

/** The same search the order screen uses: name, phone, card number or plate. */
function CustomerPicker({ onPick }: { onPick: (id: string) => void }) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const { data } = useCustomers(useDebounced(search, 250));
  const found = (data?.items ?? []).slice(0, 8);

  return (
    <Card>
      <SectionTitle>{t('floor.whoseCar')}</SectionTitle>
      <Field
        placeholder={t('neworder.findPlaceholder')}
        value={search}
        autoFocus
        onChange={(event) => setSearch(event.target.value)}
      />
      {found.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {found.map((customer) => (
            <li key={customer.id}>
              <button
                onClick={() => onPick(customer.id)}
                className="flex w-full items-center gap-3 rounded-xl border border-line bg-raised px-3.5 py-2.5 text-left transition hover:border-brand/50"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink">{customer.name}</span>
                  <span className="tabular block text-[0.7rem] text-ink-faint">
                    {customer.loyaltyId} · {customer.phone}
                  </span>
                </span>
                {customer.tierCode && <TierBadge code={customer.tierCode} size="sm" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Taking a car in.
 *
 * Deliberately short: at the door there is a customer standing next to you,
 * so this asks only what cannot be reconstructed later — whose car, which
 * car, what was agreed, by when, and what the paintwork looked like on
 * arrival. Money is the till's problem, hours from now.
 */
export function AcceptCarPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const customerId = params.get('customer') ?? '';
  const bookingId = params.get('booking');
  const { data: customer } = useCustomer(customerId || undefined);
  const { data: services } = useServices();
  const assignees = useAssignees(true);
  const accept = useAcceptCar();
  const { data: rules } = useInspectionRules();
  const words = useInspectionWords();
  const [capture, setCapture] = useState<CaptureState>({
    inspectionId: null, missing: [...REQUIRED_SLOTS.INTAKE], busy: false, photoCount: 0,
  });
  const errorText = useApiError();

  // What was agreed on the phone, so the counter is not working blind.
  const { data: bookings } = useBookings('open');
  const booking = bookingId
    ? bookings?.items.find((b) => b.id === bookingId)
    : undefined;

  // `undefined` until someone taps a car; a deliberate deselect is `null`.
  const [pickedVehicleId, setVehicleId] = useState<string | null | undefined>(undefined);
  const [promised, setPromised] = useState<Date | null>(null);
  const [pickerKey, setPickerKey] = useState(0);
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<Row[]>([newRow()]);

  // Arriving against a booking made for one technician: their name goes on
  // the work straight away, once, and can still be changed.
  const prefilled = useRef<string | null>(null);
  useEffect(() => {
    const assignee = booking?.assignedAdminId;
    if (!booking || !assignee || prefilled.current === booking.id) return;
    prefilled.current = booking.id;
    setRows((prev) => prev.map((row) => (row.performedByAdminId ? row : { ...row, performedByAdminId: assignee })));
  }, [booking]);

  const fromBooking = booking && (
    <Card className="mb-4">
      <SectionTitle>{t('booking.fromBooking')}</SectionTitle>
      <p className="text-sm font-semibold text-ink">{booking.name}</p>
      {booking.phone && (
        <p className="tabular mt-0.5 text-sm text-ink-dim">{booking.phone}</p>
      )}
      {booking.vehicleNote && <p className="mt-1 text-sm text-ink-dim">{booking.vehicleNote}</p>}
      {booking.request && <p className="mt-0.5 text-sm text-ink-dim">{booking.request}</p>}
      {!booking.customerId && (
        <p className="mt-2 text-xs leading-relaxed text-ink-faint">{t('booking.findOrCreate')}</p>
      )}
    </Card>
  );

  if (!customerId) {
    return (
      <div className="mx-auto max-w-xl pb-16">
        <h1 className="mb-5 font-display text-[1.75rem] font-bold tracking-tight text-ink sm:text-[2rem]">
          {t('floor.acceptTitle')}
        </h1>
        {fromBooking}
        <CustomerPicker onPick={(id) => setParams(
          bookingId ? { customer: id, booking: bookingId } : { customer: id },
        )} />
      </div>
    );
  }

  const ready = rows.filter(named);
  // Photos at the door hold the car back only while the studio requires them.
  const photosBlock = Boolean(rules?.intakeRequired) && (!capture.inspectionId || capture.missing.length > 0);
  const vehicles = customer?.vehicles ?? [];
  // A customer with one car brought that car, so nobody should have to tap it.
  const vehicleId = pickedVehicleId !== undefined
    ? pickedVehicleId
    : vehicles.length === 1 ? vehicles[0]!.id : null;

  const submit = (photosLater: boolean) => accept.mutate({
    customerId,
    vehicleId,
    // Sent whenever something was photographed, required or not.
    inspectionId: rules?.intakeRequired || capture.photoCount > 0 || photosLater ? capture.inspectionId : null,
    photosLater,
    promisedAt: promised ? promised.toISOString() : null,
    ...(notes.trim() ? { notes: notes.trim() } : {}),
    lines: ready.map((row) => ({
      serviceId: row.choice!.kind === 'service' ? row.choice!.serviceId : null,
      customName: row.choice!.kind === 'custom' ? row.choice!.customName.trim() : null,
      quantity: row.quantity,
      performedByAdminId: row.performedByAdminId,
    })),
    // Closes the booking this car was expected against.
    bookingId,
  }, { onSuccess: () => navigate('/floor') });

  return (
    <div className="mx-auto max-w-xl pb-24">
      <header className="mb-5">
        <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">
          {t('floor.acceptTitle')}
        </h1>
        {customer && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold text-ink">{customer.name}</span>
            <TierBadge code={customer.loyalty.tier.code} size="sm" />
            <button
              onClick={() => setParams(bookingId ? { booking: bookingId } : {})}
              className="-my-2 inline-flex min-h-11 items-center px-1 text-xs text-ink-faint transition hover:text-ink-dim"
            >
              {t('neworder.changeCustomer')}
            </button>
          </div>
        )}
      </header>

      {accept.error instanceof ApiError && (
        <div className="mb-4"><Alert>{errorText(accept.error) ?? accept.error.message}</Alert></div>
      )}

      {fromBooking}

      <div className="space-y-4">
        {vehicles.length > 0 && (
          <Card>
            <SectionTitle>{t('floor.whichCar')}</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {vehicles.map((vehicle) => {
                const active = vehicleId === vehicle.id;
                return (
                  <button
                    key={vehicle.id}
                    onClick={() => setVehicleId(active ? null : vehicle.id)}
                    aria-pressed={active}
                    className={`rounded-xl border px-3.5 py-2.5 text-left text-sm transition ${
                      active ? 'border-brand bg-brand-wash text-ink'
                             : 'border-line bg-raised text-ink-dim hover:border-ink-faint'
                    }`}
                  >
                    <span className="block font-medium">{vehicle.make} {vehicle.model}</span>
                    {vehicle.licensePlate && (
                      <span className="tabular block text-[0.7rem] text-ink-faint">
                        {vehicle.licensePlate}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        <Card>
          <SectionTitle>{t('inspection.intakeTitle')}</SectionTitle>
          {vehicleId
            ? (
              <InspectionCapture
                target={{ kind: 'INTAKE', vehicleId }}
                vehicle={{ id: vehicleId, customerId, vin: vehicles.find((v) => v.id === vehicleId)?.vin ?? null }}
                onState={setCapture}
              />
            )
            : <p className="text-sm text-ink-dim">{t('inspection.pickCarFirst')}</p>}
        </Card>

        <Card>
          <SectionTitle action={
            <Button variant="ghost" size="sm" onClick={() => setRows((prev) => [...prev, newRow()])}>
              {t('floor.addWork')}
            </Button>
          }>{t('floor.whatToDo')}</SectionTitle>

          {rows.length === 0 ? (
            <EmptyState title={t('floor.noWork')} />
          ) : (
            <div className="space-y-2.5">
              {rows.map((row) => (
                <div key={row.key} className="rounded-xl border border-line bg-raised p-3">
                  <ServicePicker
                    label={t('neworder.service')}
                    services={services?.items ?? []}
                    value={row.choice}
                    onChange={(choice) => setRows((prev) => prev.map((r) =>
                      r.key === row.key ? { ...r, choice } : r))}
                  />

                  {row.choice?.kind === 'custom' && (
                    <div className="mt-2.5">
                      <Field
                        label={t('floor.oneOffName')}
                        value={row.choice.customName}
                        autoFocus={row.choice.customName === ''}
                        onChange={(event) => setRows((prev) => prev.map((r) =>
                          r.key === row.key
                            ? { ...r, choice: { kind: 'custom', customName: event.target.value } }
                            : r))}
                      />
                    </div>
                  )}

                <div className="mt-2.5 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                  <Select
                    label={t('floor.who')}
                    value={row.performedByAdminId ?? ''}
                    onChange={(event) => setRows((prev) => prev.map((r) =>
                      r.key === row.key
                        ? { ...r, performedByAdminId: event.target.value || null }
                        : r))}
                  >
                    <option value="">{t('floor.unassigned')}</option>
                    {assignees(row.performedByAdminId).map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.firstName} {person.lastName}
                      </option>
                    ))}
                  </Select>

                  <button
                    onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                    className="min-h-10 self-end rounded-lg border border-line px-3 text-xs text-ink-faint transition hover:text-ink"
                  >
                    {t('common.remove')}
                  </button>
                </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <SectionTitle action={promised && (
            <button type="button" onClick={() => { setPromised(null); setPickerKey((k) => k + 1); }} className="min-h-9 text-xs font-semibold text-ink-dim hover:text-ink">
              {t('floor.clearPromise')}
            </button>
          )}>{t('floor.promise')}</SectionTitle>
          <DuePicker key={pickerKey} value={promised} onChange={setPromised} />
          <div className="mt-3">
            <Field
              label={t('floor.intakeNotes')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </Card>

        <Button
          size="lg"
          className="w-full"
          loading={accept.isPending && !accept.variables?.photosLater}
          disabled={ready.length === 0 || photosBlock || capture.busy}
          onClick={() => submit(false)}
        >
          {t('floor.acceptSubmit')}
        </Button>

        {/* The car can go onto the floor first and be photographed once things calm down. */}
        {photosBlock && ready.length > 0 && (
          <Button
            variant="ghost"
            className="w-full"
            loading={accept.isPending && accept.variables?.photosLater === true}
            disabled={capture.busy}
            onClick={() => submit(true)}
          >
            {t('floor.acceptPhotosLater')}
          </Button>
        )}

        {photosBlock && (
          <p className="text-center text-xs text-warning">
            {t('inspection.missing', {
              list: capture.missing.map((slot) => words.slot(slot)).join(', '),
            })}
          </p>
        )}
        {ready.length > 0 && !photosBlock && (
          <p className="text-center text-xs text-ink-faint">
            <Pill>{ready.length}</Pill> {t('floor.whatToDo').toLowerCase()}
          </p>
        )}
      </div>
    </div>
  );
}
